import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tent, AlertCircle, Mail, RefreshCw, CheckCircle2, ShieldCheck } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, verifyOtp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Email verification state
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Admin 2FA (OTP) state
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [challenge, setChallenge] = useState('');
  const [otp, setOtp] = useState('');
  const [otpInfo, setOtpInfo] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailNotVerified(false);
    setResendSuccess(false);
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.requires2fa) {
        setChallenge(result.challenge);
        setStep('otp');
        setOtpInfo('We emailed a 6-digit code to the admin inbox. It expires in 10 minutes.');
        setLoading(false);
        return;
      }
      // Redirect based on role
      if (result.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (errorDetail === 'email_not_verified') {
        setEmailNotVerified(true);
      } else {
        setError(errorDetail || 'Invalid email or password');
      }
    }
    setLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyOtp(challenge, otp, rememberDevice);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code');
      setOtp('');
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    setError('');
    setOtpInfo('');
    try {
      await authAPI.resendOtp({ challenge });
      setOtpInfo('A new code has been sent. Check the admin inbox.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend code. Please start over.');
    }
  };

  const backToLogin = () => {
    setStep('credentials');
    setOtp('');
    setChallenge('');
    setError('');
    setOtpInfo('');
    setRememberDevice(false);
  };

  const handleResendVerification = async () => {
    if (!email || !password) {
      setError('Please enter your email and password first');
      return;
    }
    
    setResendingEmail(true);
    setError('');
    setResendSuccess(false);
    
    try {
      await authAPI.resendVerificationUnauth(email, password);
      setResendSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification email');
    }
    
    setResendingEmail(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 pb-20 md:pb-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Tent className="w-7 h-7 text-primary-foreground" />
            </div>
          </Link>
          <h1 className="font-heading font-bold text-2xl mt-4">{step === 'otp' ? 'Admin verification' : 'Welcome back'}</h1>
          <p className="text-muted-foreground">{step === 'otp' ? 'Enter the code we emailed you' : 'Sign in to manage your profile'}</p>
        </div>

        {/* Admin 2FA OTP step */}
        {step === 'otp' ? (
          <div className="bg-card rounded-2xl border border-border p-6 md:p-8" data-testid="otp-step">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">Two-factor authentication keeps admin access secure.</p>
            </div>
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl text-sm" data-testid="otp-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              {otpInfo && !error && (
                <div className="flex items-center gap-2 p-3 bg-primary/10 text-primary rounded-xl text-sm" data-testid="otp-info">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  {otpInfo}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="otp">6-digit code</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="______"
                  className="h-12 rounded-xl text-center text-2xl tracking-[0.5em] font-semibold"
                  required
                  autoFocus
                  data-testid="otp-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none" data-testid="otp-remember-label">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="w-4 h-4 rounded border-border accent-[#0F5132]"
                  data-testid="otp-remember-device"
                />
                Trust this device for 30 days (skip the code next time)
              </label>
              <Button
                type="submit"
                className="w-full h-12 rounded-full"
                disabled={loading || otp.length !== 6}
                data-testid="otp-submit"
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
            </form>
            <div className="mt-5 flex items-center justify-between text-sm">
              <button type="button" onClick={backToLogin} className="text-muted-foreground hover:text-primary" data-testid="otp-back">
                ← Back to login
              </button>
              <button type="button" onClick={handleResendOtp} className="text-primary font-medium hover:underline" data-testid="otp-resend">
                Resend code
              </button>
            </div>
          </div>
        ) : (
        <>
        {/* Email Verification Required Message */}
        {emailNotVerified && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6" data-testid="email-verification-required">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 mb-1">Email Verification Required</h3>
                <p className="text-sm text-amber-700 mb-4">
                  Please verify your email address. Check your inbox for a confirmation link.
                </p>
                
                {resendSuccess ? (
                  <div className="flex items-center gap-2 text-primary text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    Verification email sent! Check your inbox.
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResendVerification}
                    disabled={resendingEmail}
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    data-testid="resend-verification-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${resendingEmail ? 'animate-spin' : ''}`} />
                    {resendingEmail ? 'Sending...' : 'Resend Verification Email'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-card rounded-2xl border border-border p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl text-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 rounded-xl"
                required
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-xl"
                required
                data-testid="login-password"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 rounded-full"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary font-medium hover:underline">
              Get Listed
            </Link>
          </div>

          <div className="text-center mt-2">
            <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary">
              Forgot your password?
            </Link>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
